import { createAppAsyncThunk } from '../appAsyncThunk';
import { history } from '../../router/BrowserRouter';
import { get, post } from 'aws-amplify/api';
import { setSignUpStep } from './slice';
import {
    SignUp,
    UserAttributes,
    ConfirmSignUpBody,
} from './types';
import {
    signIn,
    signUp,
    AuthError,
    autoSignIn,
    confirmSignUp,
    resendSignUpCode,
    updateUserAttributes,
    confirmUserAttribute,
} from 'aws-amplify/auth'

export const checkSignUpThunk = createAppAsyncThunk(
    'signUp/checkSignUpThunk', async (username: string, { dispatch, rejectWithValue }) => {
        try {
            const res = await signIn({ username, password: '123' });

            if (res.nextStep.signInStep === 'CONFIRM_SIGN_UP') {
                dispatch(resendEmailConfirmationCodeThunk(username));
            }
        } catch (e: unknown) {
            if (e instanceof Error) {
                switch (e?.message) {
                    case 'User does not exist.':
                        dispatch(setSignUpStep('createPassword'));
                        return rejectWithValue('');
                    case 'Incorrect username or password.':
                        return rejectWithValue('This user has already exist.');
                    default:
                        return rejectWithValue(e?.message);
                }
            }
        }
    });

export const signUpThunk = createAppAsyncThunk(
    'signUp/signUpThunk', async ({ username, password }: SignUp, { rejectWithValue }) => {
        try {
            const response = await signUp({
                username,
                password,
                options: {
                    userAttributes: {
                        phone_number: '',
                    },
                    autoSignIn: true,
                }
            });
            return response.userId;
        } catch (e: unknown) {
            if (e instanceof Error) {
                return rejectWithValue(e.message);
            }
        }
    });

export const confirmSignUpThunk = createAppAsyncThunk(
    'signUp/confirmSignUpThunk', async ({ username, confirmationCode }: ConfirmSignUpBody, { dispatch, rejectWithValue }) => {
        try {
            const response = await confirmSignUp({ username, confirmationCode });
            const resp = await autoSignIn();
            if (resp.nextStep.signInStep === 'DONE') {
                localStorage.setItem('auth_status', 'Authenticated');
                dispatch(setSignUpStep('personalInfo'));
                return response.userId;
            } else {
                dispatch(setSignUpStep('useSignInForm'));
                return rejectWithValue('');
            }
        } catch (e: unknown) {
            if (e instanceof AuthError) {
                if (e.name === 'AutoSignInException') {
                    dispatch(setSignUpStep('useSignInForm'));
                    return;
                } else {
                    return rejectWithValue(e.message);
                }
            } else {
                return rejectWithValue('Somthing went wrong. Please try again');
            }
        }
    });

export const confirmIncompleteSignUpThunk = createAppAsyncThunk(
    'signUp/confirmIncompleteSignUpThunk', async ({ username, confirmationCode }: ConfirmSignUpBody, { rejectWithValue }) => {
        try {
            const response = await confirmSignUp({ username, confirmationCode });
            return response.userId;
        } catch (e: unknown) {
            if (e instanceof Error) {
                return rejectWithValue(e.message);
            } else {
                return rejectWithValue('Somthing went wrong. Please try again');
            }
        }
    });

export const resendEmailConfirmationCodeThunk = createAppAsyncThunk(
    'signUp/resendConfirmationCodeThunk', async (username: string) => {
        const response = await resendSignUpCode({ username });
        return response;
    });

export const resendPhoneNumberConfirmationCodeThunk = createAppAsyncThunk(
    'signUp/resendPhoneNumberConfirmationCodeThunk', async (phoneNumber: string) => {
        // const response = await sendUserAttributeVerificationCode({ userAttributeKey: 'phone_number' });
        const response = await updateUserAttributes({ userAttributes: { phone_number: phoneNumber } })
        return response;
    });

export const updatePersonalInfoThunk = createAppAsyncThunk(
    'signUp/updatePersonalInfoThunk', async (userAttributes: UserAttributes, { dispatch, rejectWithValue }) => {
        try {
            const response = await updateUserAttributes({
                userAttributes: {
                    email: userAttributes.email,
                    address: userAttributes.address,
                    birthdate: userAttributes.birthDate,
                    family_name: userAttributes.lastName,
                    given_name: userAttributes.firstName,
                    phone_number: userAttributes.phoneNumber,
                    'custom:city': userAttributes.city,
                    'custom:state': userAttributes.state,
                    'custom:zipCode': userAttributes.zipCode,
                    'custom:country': userAttributes.country,
                    'custom:firstName': userAttributes.firstName,
                    'custom:linkedinLink': userAttributes.linkedinLink,
                    'custom:preferredName': userAttributes.preferredName,
                }
            });

            if (response.phone_number.nextStep.updateAttributeStep === 'CONFIRM_ATTRIBUTE_WITH_CODE') {
                dispatch(setSignUpStep('verifyPhoneNumber'));
            } else {
                dispatch(setSignUpStep('uploadProfilePhoto'));
            }

            return response;
        } catch (e: unknown) {
            if (e instanceof Error) {
                return rejectWithValue(e.message);
            } else {
                return rejectWithValue('Somthing went wrong. Please try again');
            }
        }
    });

export const confirmPhoneNumberThunk = createAppAsyncThunk(
    'signUp/confirmEmailThunk', async (confirmationCode: string, { rejectWithValue }) => {
        try {
            await confirmUserAttribute({ userAttributeKey: 'phone_number', confirmationCode });
        } catch (e: unknown) {
            if (e instanceof Error) {
                return rejectWithValue(e.message);
            } else {
                return rejectWithValue('Somthing went wrong. Please try again');
            }
        }
    });

export const getCountriesThunk = createAppAsyncThunk(
    'signUp/getCountriesThunk', async () => {

        const restOperation = await get({
            apiName: 'countries',
            path: '/countries/{proxy+}',
        }).response;

        const response = await restOperation.body.json();
        return response as any;
    });

export const getStatesThunk = createAppAsyncThunk(
    'signUp/getStatesThunk', async () => {

        const restOperation = await get({
            apiName: 'states',
            path: '/states/{proxy+}',
        }).response;

        const response = await restOperation.body.json();
        return response as any;
    });

export const getCitiesThunk = createAppAsyncThunk(
    'signUp/getCitiesThunk', async (city: string) => {

        const restOperation = await get({
            apiName: 'city',
            path: '/city/{proxy+}',
            options: {
                queryParams: { city }
            }
        }).response;

        const response = await restOperation.body.json();
        return response as any;
    });

export const getExpertiesThunk = createAppAsyncThunk(
    'signUp/getExpertiesThunk', async () => {

        const restOperation = await get({
            apiName: 'expertise',
            path: '/expertise/{proxy+}',
        }).response;

        const response = await restOperation.body.json();
        return response as any;
    });

export const getSkillsThunk = createAppAsyncThunk(
    'signUp/getSkillsThunk', async () => {

        const restOperation = await get({
            apiName: 'skills',
            path: '/skills/{proxy+}',
        }).response;

        const response = await restOperation.body.json();
        return response as any;
    });

export const setProfileDataThunk = createAppAsyncThunk(
    'signUp/setProfileDataThunk', async (body: any) => {

        const restOperation = post({
            apiName: 'profile',
            path: '/profile/{proxy+}',
            options: { body }
        });

        const response = await restOperation.response;
        return response.body.json();
    });

export const getUniversitiesThunk = createAppAsyncThunk(
    'signUp/getUniversitiesThunk', async (university: string) => {

        const restOperation = await get({
            apiName: 'university',
            path: '/university/{proxy+}',
            options: {
                queryParams: { university }
            }
        }).response;

        const response = await restOperation.body.json();
        return response as any;
    });

export const getJobListThunk = createAppAsyncThunk(
    'signUp/getJobListThunk', async (job: string) => {

        const restOperation = await get({
            apiName: 'job',
            path: '/job/{proxy+}',
            options: {
                queryParams: { job }
            }
        }).response;

        const response = await restOperation.body.json();
        return response as any;
    });

export const kycThunk = createAppAsyncThunk(
    'signUp/kycThunk', async (body: any) => {
        const restOperation = await post({
            apiName: 'kyc',
            path: '/kyc/{proxy+}',
            options: { body }
        }).response;

        const response = await restOperation.body.json();
        return response as any;
    });
